// @ts-nocheck
import React, {useState} from 'react';
import {View, StyleSheet} from 'react-native';
import {Approval} from '../../../@types/Approval';
import {Card, TouchableRipple} from 'react-native-paper';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {useSelector} from 'react-redux';
import {RootStore} from '../../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../service/flashMessage';
import {residentApiWithAuth} from '../../../api/resident';
import Text from '../../../components/Text';
import Ionicons from '@expo/vector-icons/Ionicons';
import Feather from '@expo/vector-icons/Feather';

/** Maps an approval status string to an icon + colour pair. */
function statusIcon(status?: string): {name: string; color: string} {
  switch (status) {
    case 'Approved':
      return {name: 'checkmark-circle', color: '#16a34a'};
    case 'Denied':
      return {name: 'close-circle', color: '#dc2626'};
    case 'Pending':
    default:
      return {name: 'time-outline', color: '#d97706'};
  }
}

/** Small pill chip for resident / management status. */
function StatusChip({label, status}: {label: string; status?: string}) {
  const {name, color} = statusIcon(status);
  const isPending = status === 'Pending' || !status;
  const bg = isPending ? '#fef3c7' : status === 'Approved' ? '#dcfce7' : '#fee2e2';

  return (
    <View style={[chipStyles.chip, {backgroundColor: bg}]}>
      <Ionicons name={name} size={12} color={color} />
      <Text style={[chipStyles.label, {color}]}>{label}</Text>
    </View>
  );
}

const chipStyles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    gap: 4,
    alignSelf: 'flex-start',
  },
  label: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'capitalize',
  },
});

/** Formats the approval type for display. */
function formatType(type?: string) {
  if (!type) return 'APPROVAL';
  return type.replace(/_/g, ' ');
}

export default function ApprovalItem({
  approval,
  approvalClickHandler,
  refreshList,
}: {
  approval: Approval;
  approvalClickHandler: Function;
}) {
  const [_approval, _setApproval] = useState(approval);
  const unitLink = useSelector(
    (state: RootStore) => state.units.unitLinks,
  )?.filter(link => link.unit.id === _approval?.unit?.unitId)[0];
  const [requiresResidentApproval, setRequiresResidentApproval] = useState(
    unitLink?.allowedtoApprove &&
      _approval.residentApprovalStatus === 'Pending',
  );
  const [loading, setLoading] = useState(false);
  const {user} = useSelector((state: RootStore) => state.auth);

  async function handleApprove(id: string, approve: boolean) {
    setLoading(true);
    try {
      const res = await residentApiWithAuth().post('/approvals', {id, approve});
      const result = res.data;
      if (result.errors) {
        showErrorMessage('ApprovalItem: ' + result.errors[0]);
      } else {
        !!refreshList && refreshList();
        _setApproval({
          ...approval,
          residentApprovalStatus: approve ? 'Approved' : 'Denied',
          residentApprovalDateTime: new Date().toISOString(),
          residentApprovalName: `${user.firstName} ${user.lastName}`,
        });
        setRequiresResidentApproval(false);
        showSuccessMessage('Your response has been sent');
      }
    } catch (error) {
      showErrorMessage('ApprovalItem - 2: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  const isPending = _approval.residentApprovalStatus === 'Pending';

  return (
    <Card onPress={() => approvalClickHandler()} style={[styles.card, isPending && styles.pendingCard]}>
      <View style={styles.container}>
        {/* ── Header row: unit name + date ── */}
        <View style={styles.headerRow}>
          <View style={styles.titleBlock}>
            <View style={styles.typeRow}>
              <View style={styles.typeBadge}>
                <Ionicons name="document-text-outline" size={11} color="#6366f1" />
                <Text style={styles.typeBadgeText}>{formatType(_approval?.type)}</Text>
              </View>
            </View>
            <Text numberOfLines={1} style={styles.unitText}>
              {`Unit ${_approval.unit.unitName}`}
            </Text>
          </View>
          <View style={styles.dateChevron}>
            <Text style={styles.dateText}>
              {new Date(_approval.approvalRequestDateTime).toLocaleDateString()}
            </Text>
            <Feather name="chevron-right" size={18} color="#a1a1aa" />
          </View>
        </View>

        {/* ── Short message preview (2 lines max) ── */}
        {!!_approval.message && (
          <Text numberOfLines={2} style={styles.messageText}>
            {_approval.message}
          </Text>
        )}

        {/* ── Requester + status chips row ── */}
        <View style={styles.footerRow}>
          {!!_approval.approvalRequesterName && (
            <View style={styles.requesterRow}>
              <Feather name="user" size={11} color="#71717a" />
              <Text numberOfLines={1} style={styles.requesterText}>
                {_approval.approvalRequesterName}
              </Text>
            </View>
          )}
          <View style={styles.chipRow}>
            <StatusChip label="Unit" status={_approval.residentApprovalStatus} />
            <StatusChip label="Management" status={_approval.managementApprovalStatus} />
          </View>
        </View>

        {/* ── Inline approve / deny buttons (only when action needed) ── */}
        {loading ? (
          <LoadingSpinner />
        ) : (
          requiresResidentApproval && (
            <View style={styles.buttonContainer}>
              <TouchableRipple onPress={() => handleApprove(_approval.id, true)}>
                <View style={[styles.button, styles.approveButton]}>
                  <Ionicons name="checkmark-circle-outline" size={14} color="#fff" />
                  <Text style={styles.buttonText}>Approve</Text>
                </View>
              </TouchableRipple>
              <TouchableRipple onPress={() => handleApprove(_approval.id, false)}>
                <View style={[styles.button, styles.denyButton]}>
                  <Ionicons name="close-circle-outline" size={14} color="#fff" />
                  <Text style={styles.buttonText}>Deny</Text>
                </View>
              </TouchableRipple>
            </View>
          )
        )}
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 10,
    borderColor: '#e4e4e7',
    borderWidth: 1,
    elevation: 1,
    marginHorizontal: 12,
    marginVertical: 5,
    overflow: 'hidden',
  },
  pendingCard: {
    borderLeftColor: '#6366f1',
    borderLeftWidth: 4,
  },
  container: {
    flexDirection: 'column',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 6,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  typeRow: {
    flexDirection: 'row',
  },
  typeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#eef2ff',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignSelf: 'flex-start',
  },
  typeBadgeText: {
    color: '#6366f1',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  unitText: {
    color: '#18181b',
    fontSize: 14,
    fontWeight: '700',
    marginTop: 2,
  },
  dateChevron: {
    alignItems: 'flex-end',
    gap: 4,
    flexShrink: 0,
  },
  dateText: {
    color: '#71717a',
    fontSize: 10,
    lineHeight: 14,
    textAlign: 'right',
  },
  messageText: {
    color: '#52525b',
    fontSize: 13,
    lineHeight: 18,
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 2,
  },
  requesterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flex: 1,
    minWidth: 0,
  },
  requesterText: {
    color: '#71717a',
    fontSize: 11,
    flexShrink: 1,
  },
  chipRow: {
    flexDirection: 'row',
    gap: 6,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 6,
  },
  approveButton: {
    backgroundColor: '#16a34a',
  },
  denyButton: {
    backgroundColor: '#dc2626',
  },
  buttonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '700',
  },
});
